import { useMemo } from 'react'
import { RowContainerWithScroll } from '@/components/RowContainerWithScroll'
import { OtherAvailabilityTitleRow } from '@/Reservations/Search/OtherAvailability'
import { useLocales } from '@sevenrooms/locales'
import { useTheme } from '@sevenrooms/ui-kit'
import { Box } from '@sevenrooms/ui-kit/layout'
import type { ThemeSpacing } from '@sevenrooms/ui-kit/theme'
import { Text } from '@sevenrooms/ui-kit/typography'
import { getTimeslotDescription } from '../../PrimaryAvailability/ReservationAvailability'
import { ReservationTimeslot } from '../../ReservationTimeslot'
import { SkeletonBox } from '../../SkeletonBox'
import type { AvailabilityTimeWithUpSellCost } from '../../../hooks'

export interface OtherAvailabilityGroupProps {
  title: string
  titleDataTest: string
  subtitle?: string
  subtitleDataTest?: string
  onTitleClick: () => void
  onTimeSlotClick: (slot: AvailabilityTimeWithUpSellCost) => void
  timeslots: AvailabilityTimeWithUpSellCost[]
  currencyCode: string
  scrollLeftLabel: string
  scrollRightLabel: string
  scrollRightMargin?: ThemeSpacing
  isFetching: boolean
  noAvailabilityMessage?: string
  numLoadingSkeleton?: number
  shouldUsePrimaryColor?: boolean
}

const OTHER_AVAILABILITY_TIMESLOT_WIDTH = 150
const NUMBER_OF_SKELETON_LOADERS = 8

export function OtherAvailability({
  title,
  titleDataTest,
  subtitle,
  subtitleDataTest,
  onTitleClick,
  onTimeSlotClick,
  timeslots,
  currencyCode,
  scrollLeftLabel,
  scrollRightLabel,
  isFetching,
  scrollRightMargin,
  noAvailabilityMessage,
  numLoadingSkeleton,
  shouldUsePrimaryColor,
}: OtherAvailabilityGroupProps) {
  const { formatMessage } = useLocales()

  const otherAvailabilityTitle = useMemo(
    () => (
      <OtherAvailabilityTitleRow
        title={title}
        subtitle={subtitle}
        titleDataTest={titleDataTest}
        subtitleDataTest={subtitleDataTest}
        onTitleClick={onTitleClick}
      />
    ),
    [onTitleClick, subtitle, subtitleDataTest, title, titleDataTest]
  )

  if (!timeslots?.length && !isFetching && !noAvailabilityMessage) {
    return otherAvailabilityTitle
  }

  const isNoAvailability = timeslots.length === 0 && !isFetching && noAvailabilityMessage

  return (
    <RowContainerWithScroll
      label={otherAvailabilityTitle}
      data-test="other-availability"
      scrollLeftLabel={scrollLeftLabel}
      scrollRightLabel={scrollRightLabel}
      marginRight={scrollRightMargin}
      spacing="sm"
      childWidth={isNoAvailability ? '100%' : `${OTHER_AVAILABILITY_TIMESLOT_WIDTH}px`}
    >
      {isFetching ? (
        <OtherAvailabilitySkeleton numLoadingSkeleton={numLoadingSkeleton} />
      ) : (
        timeslots.map(timeSlotInfo => (
          <ReservationTimeslot
            variant={shouldUsePrimaryColor ? 'primary' : 'secondary'}
            width={`${OTHER_AVAILABILITY_TIMESLOT_WIDTH}px`}
            key={`${timeSlotInfo.time}${timeSlotInfo.accessPersistentId ?? ''}`}
            timeslotDescription={getTimeslotDescription(
              formatMessage,
              timeSlotInfo.publicTimeSlotDescription,
              timeSlotInfo.showCost,
              (timeSlotInfo.cost ?? 0) + timeSlotInfo.includedUpgradesCost,
              timeSlotInfo.chargeType,
              timeSlotInfo.duration,
              currencyCode,
              timeSlotInfo.fees
            )}
            time={timeSlotInfo.time}
            onClick={() => {
              onTimeSlotClick(timeSlotInfo)
            }}
          />
        ))
      )}
      {isNoAvailability && (
        <Text textStyle="body1" data-test="no-availability-message">
          {noAvailabilityMessage}
        </Text>
      )}
    </RowContainerWithScroll>
  )
}

function OtherAvailabilitySkeleton({ numLoadingSkeleton }: { numLoadingSkeleton?: number }) {
  const { fieldHeight } = useTheme()
  return Array.from({ length: numLoadingSkeleton || NUMBER_OF_SKELETON_LOADERS }, (_v, i) => i).map(i => (
    <Box key={i}>
      <SkeletonBox width={`${OTHER_AVAILABILITY_TIMESLOT_WIDTH}px`} height={fieldHeight.l} />
    </Box>
  ))
}
